import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ExternalLink, Globe, Menu, Phone, X } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LANGS } from "@/i18n/translations";
import { useSettings } from "@/lib/hooks";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ONLINE_BASVURU_URL = "https://entanet.com.tr/online_basvuru/#/";

const NAV_ITEMS = [
  { to: "/", key: "nav.home" },
  { to: "/tarifeler", key: "nav.packages" },
  { to: "/kampanyalar", key: "nav.campaigns" },
  { to: "/hizmetler", key: "nav.services" },
  { to: "/blog", key: "nav.blog" },
  { to: "/sss", key: "nav.faq" },
  { to: "/iletisim", key: "nav.contact" },
];

export const Logo = ({ dark }) => (
  <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2 shrink-0">
    <img src="/entalogo.png" alt="EntaNET" className="h-9 w-auto" />
  </Link>
);

export default function Navbar() {
  const { t, lang, setLang } = useLang();
  const { data: settings } = useSettings();
  const [open, setOpen] = useState(false);
  const onlineUrl = settings?.online_islemler_url || "https://oim.entanet.com.tr";
  const phone = settings?.phone || "+90 324 714 17 04";

  return (
    <header className="fixed top-0 z-50 w-full bg-[#101436]/40 backdrop-blur-md" data-testid="navbar">
      {/* Top utility bar */}
      <div className="hidden md:block" style={{ backgroundColor: "rgba(16, 20, 54, 0.40)" }}>
        <div className="mx-auto flex h-9 max-w-7xl items-center justify-between px-4 text-xs sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="navbar-individual" className="font-light text-white transition-colors hover:text-white" style={{ borderBottom: "2px solid #ff9800", paddingBottom: "2px" }}>
              {t("nav.individual")}
            </Link>
            <span className="text-zinc-500">|</span>
            <Link to="/kurumsal" className="font-light text-zinc-400 transition-colors hover:text-white">
              {t("nav.corporate")}
            </Link>
          </div>
          <a
            href={`tel:${phone.replace(/\s/g, "")}`}
            className="flex items-center gap-1.5 text-zinc-300 transition-colors hover:text-white"
            data-testid="navbar-phone"
            style={{ fontSize: "0.72rem" }}
          >
            <Phone className="h-3.5 w-3.5" />
            {phone}
          </a>
        </div>
      </div>

      {/* Main nav */}
      <div className="container mx-auto flex h-[68px] items-center justify-between px-3 sm:px-6 lg:px-8">
        <Logo />

        <nav className="hidden items-center gap-1 lg:flex" data-testid="navbar-links">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                  isActive ? "text-white" : "text-zinc-200 hover:text-white"
                }`
              }
              data-testid={`nav-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
            >
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={ONLINE_BASVURU_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-link-online-basvuru"
            className="flex items-center gap-1 rounded-full bg-[#ff9800]/15 px-4 py-2 text-sm font-bold text-[#ff9800] transition-colors duration-200 hover:bg-[#ff9800]/25"
          >
            {t("nav.onlineBasvuru")}
            <ExternalLink className="h-3 w-3" />
          </a>

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="language-switcher"
              className="flex items-center gap-1.5 rounded-full border border-white/20 bg-white/5 px-2.5 py-2 text-sm font-semibold text-white transition-colors hover:border-white/40"
            >
              <Globe className="h-4 w-4" />
              {LANGS.find((l) => l.code === lang)?.short}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {LANGS.map((l) => (
                <DropdownMenuItem
                  key={l.code}
                  data-testid={`language-option-${l.code}`}
                  onClick={() => setLang(l.code)}
                  className={lang === l.code ? "bg-zinc-100 font-bold" : ""}
                >
                  {l.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            href={onlineUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="online-islemler-button"
            className="hidden items-center gap-1.5 rounded-full border border-white/30 px-5 py-2 text-sm font-semibold text-white transition-all hover:bg-[#4F46E5] hover:border-[#4F46E5] sm:flex"
          >
            {t("nav.online")}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="bg-[#101436] px-4 py-4 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rounded-xl px-4 py-3 text-sm font-medium transition-colors ${
                    isActive ? "bg-white/10 text-[#ff9800]" : "text-zinc-300 hover:bg-white/5"
                  }`
                }
                onClick={() => setOpen(false)}
              >
                {t(item.key)}
              </NavLink>
            ))}
            <a
              href={ONLINE_BASVURU_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex items-center justify-center gap-1.5 rounded-full bg-[#ff9800] px-5 py-3 text-sm font-bold text-[#1d1d1f]"
              onClick={() => setOpen(false)}
            >
              {t("nav.onlineBasvuru")}
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </nav>
      )}
    </header>
  );
}
